import React, { useState } from 'react';
import { AttentionItem, GmailMessage } from '../../domain/types';
import { useZunelo } from '../../context/ZuneloContext';
import { useRightWorkspaceSidebar } from '../layout/RightWorkspaceSidebar';
import { buildGmailWebUrl } from './gmailWebUrl';
import { GmailCardHeader } from './GmailCardHeader';
import { GmailCardExpanded } from './GmailCardExpanded';
import { GmailDetailModal } from './GmailDetailModal';
import { useDetailSurfaceParam } from '../detail/useDetailSurfaceParam';

interface GmailCardProps {
  item: AttentionItem;
  isSelected?: boolean;
}

export const GmailCard: React.FC<GmailCardProps> = ({ item: itemProp, isSelected = false }) => {
  const {
    selectItem,
    openContextSurface,
    isItemExpanded,
    toggleItemExpanded,
    markGmailRead,
    replyToGmail,
    generateGmailThreadSummary,
    gmailSummaryBusy,
    generateGmailReplyDraft,
    navigateToItem,
    getJiraItemByKey,
    getItemById,
    currentUser,
    errors,
  } = useZunelo();

  const item = getItemById(itemProp.id) || itemProp;
  const isExpanded = isItemExpanded(item.id);
  const { restore: restoreRightSidebar } = useRightWorkspaceSidebar();
  const [isDetailOpen, setIsDetailOpen] = useDetailSurfaceParam(item.id);
  const [replyDraft, setReplyDraft] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);
  const sendError = errors.gmail;

  // Fallback if item does not have thread messages attached
  const threadMessages: GmailMessage[] =
    item.gmailMessages && item.gmailMessages.length > 0
      ? item.gmailMessages
      : [
          {
            id: item.rawGmailMessage?.id || item.id,
            threadId: item.rawGmailMessage?.threadId || item.id,
            from: {
              name: item.fromName || item.primary,
              email: item.fromEmail || item.primary,
              initials: item.initials || 'GM',
              color: item.color || 'var(--z-bad)',
            },
            to: item.to || '',
            subject: item.title,
            bodyParagraphs: item.body || [],
            quotedHead: item.quotedHead,
            quotedText: item.quoted,
            kind: (item.kind as 'Interno' | 'Aviso' | 'Externo') || 'Interno',
            unread: Boolean(item.unread),
            blocksWork: Boolean(item.blocksWork),
            receivedAt: item.updatedAt || new Date().toISOString(),
          },
        ];

  // Expanding a thread must not mark it read as a side effect — see
  // openspec/changes/provider-item-interaction-ux (honest controls: no
  // presentation action may silently mutate provider state). Marking read
  // is its own explicit action below.
  const handleCardClick = () => {
    selectItem(item.id);
    toggleItemExpanded(item.id);
  };

  const handleToggleExpanded = (event: React.MouseEvent) => {
    event.stopPropagation();
    selectItem(item.id);
    toggleItemExpanded(item.id);
  };

  const handleMarkRead = (event: React.MouseEvent) => {
    event.stopPropagation();
    const unreadMsgs = threadMessages.filter((m) => m.unread);
    if (unreadMsgs.length > 0) {
      unreadMsgs.forEach((m) => markGmailRead(m.id));
    } else {
      markGmailRead(item.id);
    }
  };

  const handleSend = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const trimmed = replyDraft.trim();
    if (!trimmed || isSending || isDrafting) return;
    // Replying always targets the thread's last message
    const lastMsg = threadMessages[threadMessages.length - 1];
    const targetId = lastMsg?.id || item.id;
    setIsSending(true);
    try {
      const ok = await replyToGmail(targetId, trimmed);
      // Only clear the composer after a confirmed successful send; a
      // failure keeps the draft text intact so the user can retry.
      if (ok) setReplyDraft('');
    } finally {
      setIsSending(false);
    }
  };

  const handleGenerateDraft = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isDrafting || isSending) return;
    const idea = replyDraft.trim();
    if (!idea) return;
    setIsDrafting(true);
    try {
      const generated = await generateGmailReplyDraft(item.id, idea);
      if (generated) setReplyDraft(generated);
    } finally {
      setIsDrafting(false);
    }
  };

  const linkedTicket = item.ticket ? getJiraItemByKey(item.ticket) : undefined;
  const gmailWebUrl = buildGmailWebUrl(
    threadMessages[0]?.threadId || item.rawGmailMessage?.threadId,
  );

  const gmailExpandedProps = {
    item,
    threadMessages,
    currentUserEmail: currentUser?.email,
    linkedTicket,
    onNavigateToJira: (id: string) => navigateToItem('jira', id),
    replyDraft,
    onReplyDraftChange: setReplyDraft,
    onSend: handleSend,
    onGenerateDraft: handleGenerateDraft,
    isSending,
    isDrafting,
    sendError,
    summaryBusy: Boolean(gmailSummaryBusy[item.id]),
    onGenerateSummary: () => void generateGmailThreadSummary(item.id),
  };

  return (
    <>
      <article
        data-testid="gmail-card"
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          padding: '15px 18px',
          borderRadius: 18,
          cursor: 'pointer',
          overflow: 'hidden',
          background: isSelected ? 'var(--z-accent-wash)' : 'var(--z-surface)',
          border: 'none',
          boxShadow: isSelected ? '0 4px 16px var(--z-sh-6)' : '0 1px 2px var(--z-sh-4)',
          transition: 'all 0.2s ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            background: item.blocksWork
              ? 'var(--z-bad)'
              : item.unread
                ? 'var(--z-warn)'
                : 'var(--z-dim)',
          }}
        />

        <div onClick={handleCardClick} style={{ cursor: 'pointer' }}>
          <GmailCardHeader
            item={item}
            isExpanded={isExpanded}
            messageCount={threadMessages.length}
            gmailWebUrl={gmailWebUrl}
            onMarkRead={handleMarkRead}
            onOpenContext={(e) => {
              e.stopPropagation();
              openContextSurface(item.id);
              restoreRightSidebar();
            }}
            onOpenDetail={(e) => {
              e.stopPropagation();
              setIsDetailOpen(true);
            }}
            onToggleExpanded={handleToggleExpanded}
          />

          <h3
            style={{
              margin: '0 0 9px',
              fontSize: 15,
              fontWeight: 700,
              lineHeight: 1.4,
              letterSpacing: '-0.015em',
              color: 'var(--z-text-2)',
            }}
          >
            {item.title}
          </h3>

          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--z-muted-3)' }}>
            {item.ai}
          </p>
        </div>

        {isExpanded && <GmailCardExpanded {...gmailExpandedProps} />}
      </article>

      {isDetailOpen ? (
        <GmailDetailModal
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          onNavigateToLinkedTicket={(id) => navigateToItem('jira', id)}
          {...gmailExpandedProps}
        />
      ) : null}
    </>
  );
};
